import { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Send, CheckCircle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const InquiryForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    email: '',
    subject: '',
    description: '',
    preferred_contact_time: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Prepare data - ensure preferred_contact_time is not empty string if not selected
      const submitData = {
        ...formData,
        preferred_contact_time: formData.preferred_contact_time || ""
      };
      
      const response = await axios.post(`${API}/inquiries`, submitData);
      
      if (response.data.success) {
        setIsSubmitted(true);
        toast.success('Inquiry submitted successfully!', {
          description: 'We will contact you shortly.'
        });
        // Reset form
        setFormData({
          full_name: '',
          phone: '',
          email: '',
          subject: '',
          description: '',
          preferred_contact_time: ''
        });
      }
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      let errorMsg = 'Please try again or contact us directly.';
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          errorMsg = error.response.data.detail.map(d => d.msg).join(', ');
        } else {
          errorMsg = error.response.data.detail;
        }
      }
      toast.error('Failed to submit inquiry', {
        description: errorMsg
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="bg-white p-8 lg:p-12 border border-gray-100 shadow-lg text-center" data-testid="inquiry-success">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-10 h-10 text-green-600" />
        </div>
        <h3 
          className="text-2xl font-semibold text-[#0B1C2D] mb-4"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          Inquiry Submitted Successfully
        </h3>
        <p className="text-[#4A5568] mb-6">
          Thank you for your inquiry. Our team will review your submission and contact you at your preferred time.
        </p>
        <Button 
          onClick={() => setIsSubmitted(false)}
          variant="outline"
          className="border-[#0B1C2D] text-[#0B1C2D] hover:bg-[#0B1C2D] hover:text-white"
        >
          Submit Another Inquiry
        </Button>
      </div>
    );
  }

  return (
    <form 
      onSubmit={handleSubmit}
      className="bg-white p-8 lg:p-12 border border-gray-100 shadow-lg"
      data-testid="inquiry-form"
    >
      <h3 
        className="text-2xl font-semibold text-[#0B1C2D] mb-2"
        style={{ fontFamily: "'Playfair Display', serif" }}
      >
        Submit Legal Query
      </h3>
      <div className="w-16 h-1 bg-[#C6A34A] mb-6" />
      <p className="text-[#718096] text-sm mb-8">
        Fill out the form below and we will get back to you as soon as possible.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Full Name */}
        <div className="space-y-2">
          <Label htmlFor="full_name" className="text-[#2B2B2B]">Full Name *</Label>
          <Input
            id="full_name"
            name="full_name"
            value={formData.full_name}
            onChange={handleChange}
            placeholder="Enter your full name"
            required
            className="bg-[#F8F6F2] border-gray-200 focus:border-[#0B1C2D] focus:ring-[#0B1C2D]"
            data-testid="input-full-name"
          />
        </div>

        {/* Phone */}
        <div className="space-y-2">
          <Label htmlFor="phone" className="text-[#2B2B2B]">Phone Number *</Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            placeholder="+91 XXXXX XXXXX"
            required
            className="bg-[#F8F6F2] border-gray-200 focus:border-[#0B1C2D] focus:ring-[#0B1C2D]"
            data-testid="input-phone"
          />
        </div>

        {/* Email */}
        <div className="space-y-2">
          <Label htmlFor="email" className="text-[#2B2B2B]">Email Address *</Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="your@email.com"
            required
            className="bg-[#F8F6F2] border-gray-200 focus:border-[#0B1C2D] focus:ring-[#0B1C2D]"
            data-testid="input-email"
          />
        </div>

        {/* Preferred Contact Time */}
        <div className="space-y-2">
          <Label htmlFor="preferred_contact_time" className="text-[#2B2B2B]">Preferred Contact Time</Label>
          <select
            id="preferred_contact_time"
            name="preferred_contact_time"
            value={formData.preferred_contact_time}
            onChange={handleChange}
            className="flex h-10 w-full rounded-md border border-gray-200 bg-[#F8F6F2] px-3 py-2 text-sm focus:border-[#0B1C2D] focus:ring-1 focus:ring-[#0B1C2D] focus:outline-none"
            data-testid="select-contact-time"
          >
            <option value="">Select preferred time</option>
            <option value="morning">Morning (9 AM - 12 PM)</option>
            <option value="afternoon">Afternoon (12 PM - 4 PM)</option>
            <option value="evening">Evening (4 PM - 7 PM)</option>
            <option value="any">Any Time</option>
          </select>
        </div>

        {/* Subject */}
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="subject" className="text-[#2B2B2B]">Subject *</Label>
          <Input
            id="subject"
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            placeholder="Brief subject of your legal matter"
            required
            className="bg-[#F8F6F2] border-gray-200 focus:border-[#0B1C2D] focus:ring-[#0B1C2D]"
            data-testid="input-subject"
          />
        </div>

        {/* Description */}
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="description" className="text-[#2B2B2B]">Brief Description of Matter *</Label>
          <Textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Please provide a brief description of your legal matter (minimum 10 characters)"
            required
            rows={5}
            className="bg-[#F8F6F2] border-gray-200 focus:border-[#0B1C2D] focus:ring-[#0B1C2D] resize-none"
            data-testid="input-description"
          />
        </div>
      </div>

      {/* Submit Button */}
      <Button 
        type="submit"
        disabled={isSubmitting}
        className="w-full mt-8 bg-[#0B1C2D] hover:bg-[#1A2E44] text-white py-6 text-base font-medium"
        data-testid="submit-inquiry-btn"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Submitting...
          </>
        ) : (
          <>
            <Send className="w-5 h-5 mr-2" />
            Submit Inquiry
          </>
        )}
      </Button>

      {/* Disclaimer */}
      <p className="text-[#718096] text-xs mt-6 text-center leading-relaxed">
        Submission of this form does not create an attorney-client relationship. 
        Any information submitted is for preliminary consultation purposes only.
      </p>
    </form>
  );
};
